import VariantButton from '@/cataloguePages/productPageWithVariants/components/VariantButton';
import { useState } from 'react';
import VariantShowMoreButton from '@/cataloguePages/productPageWithVariants/components/VariantShowMoreButton';
import {
  ServerProduct,
  ServerProductVariant,
  ServerProductVariantAttribute,
} from '@/lib/janitsjar/Product';
import { ArrayElement } from '@/cataloguePages/productPageWithVariants/helpers';
import { usePathname } from 'next/navigation';

type VariantProps = {
  type: 'text' | 'image';
  attribute: ServerProductVariantAttribute;
  product: ServerProduct;
};

export default function Variant({ type, attribute, product }: VariantProps) {
  const [showAllVariants, setShowAllVariants] = useState(false);
  const pathname = usePathname();
  const basePathname = pathname
    ? pathname.lastIndexOf('/') > 0
      ? pathname.substring(0, pathname.lastIndexOf('/'))
      : pathname
    : '';
  const currentVariant = product.variants?.variants.find(
    (v) => v.sku === product.sku
  );
  const currentVariantIndex =
    attribute.values.findIndex(
      (v) => v.value === currentVariant?.attributes[attribute.code]
    ) + 1;

  const DEFAULT_VARIANT_COUNT =
    type === 'text'
      ? Math.max(4, currentVariantIndex)
      : Math.max(4, currentVariantIndex);

  const shownVariants = attribute.values.slice(
    0,
    showAllVariants ? attribute.values.length : DEFAULT_VARIANT_COUNT
  );

  const hasMoreVariants = attribute.values.length > DEFAULT_VARIANT_COUNT;

  const getVariantLink = (variant: ArrayElement<typeof shownVariants>) => {
    const variantValue = variant.value;
    const currentProductVariant = product.variants?.variants.find(
      (v) => v.sku === product.sku
    );

    // First try to find a variant that matches all current attributes plus the new selected one
    let newSku = product.variants?.variants.find((v) => {
      if (!currentProductVariant)
        return v.attributes[attribute.code] === variantValue;

      // Check if all current attributes match (except the one being changed)
      return Object.entries(currentProductVariant.attributes).every(
        ([key, value]) => {
          // For the attribute being changed, use the new value
          if (key === attribute.code) return v.attributes[key] === variantValue;
          // For other attributes, they should match the current variant
          return v.attributes[key] === value;
        }
      );
    })?.sku;

    // If no match found, fall back to just matching the selected attribute
    if (!newSku) {
      newSku = product.variants?.variants.find(
        (v) => v.attributes[attribute.code] === variantValue
      )?.sku;
    }

    return `${basePathname}/${newSku}`;
  };

  const getVariantImage = (variant: ArrayElement<typeof shownVariants>) => {
    const variantValue = variant.value;
    const currentProductVariant = product.variants?.variants.find(
      (v) => v.sku === product.sku
    );

    // First try to find a variant that matches all current attributes plus the new selected one
    let newSku = product.variants?.variants.find((v) => {
      if (!currentProductVariant)
        return v.attributes[attribute.code] === variantValue;

      // Check if all current attributes match (except the one being changed)
      return Object.entries(currentProductVariant.attributes).every(
        ([key, value]) => {
          // For the attribute being changed, use the new value
          if (key === attribute.code) return v.attributes[key] === variantValue;
          // For other attributes, they should match the current variant
          return v.attributes[key] === value;
        }
      );
    })?.image;

    // If no match found, fall back to just matching the selected attribute
    if (!newSku) {
      newSku = product.variants?.variants.find(
        (v) => v.attributes[attribute.code] === variantValue
      )?.image;
    }

    return newSku;
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex gap-2">
        <span className="hd-base">Velg {attribute.name}:</span>
        {type === 'image' && (
          <span className="body-sm">
            {currentVariant?.attributes[attribute.code] ?? ''}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {shownVariants.map((variant, index) => (
          <VariantButton
            href={getVariantLink(variant)}
            key={variant.value}
            value={variant.value}
            image={getVariantImage(variant)}
            current={
              currentVariant?.attributes[attribute.code] === variant.value
            }
            type={type}
          />
        ))}
        {!showAllVariants && hasMoreVariants && (
          <VariantShowMoreButton
            name={attribute.name}
            type={type}
            onClick={() => {
              setShowAllVariants(true);
            }}
          />
        )}
      </div>
    </div>
  );
}
